import clsx from 'clsx';

import {
  condition,
  fmtPrecip,
  fmtSnow,
  fmtTemp,
  fmtWind,
  isExtended,
  modelName,
  pickAqi,
  type CellStat,
  type DayData,
  type WeatherPrefs
} from '../weather';
import WeatherIcon, { DropIcon } from './WeatherIcon';

interface Props {
  date: string; // YYYY-MM-DD
  day: DayData;
  prefs: WeatherPrefs;
  // `cell` for month cells, `header` for week headers.
  variant?: 'cell' | 'header';
  onOpen: (date: string, rect: DOMRect) => void;
}

// A day's condition icon and the figures chosen in settings. Figures that
// carry nothing (a 0% chance, no snow) are left out to keep cells quiet. Days
// past the chosen model's range are dimmed.
export default function DayWeather({ date, day, prefs, variant = 'cell', onOpen }: Props) {
  const cond = condition(day.weather_code);
  const items = prefs.cell_stats
    .map((s) => statItem(s, day, prefs))
    .filter((x): x is { key: CellStat; node: React.ReactNode } => x !== null);
  if (!cond && items.length === 0) return null;

  const compact = variant === 'cell';
  const extended = isExtended(date, day, prefs);
  return (
    <button
      type="button"
      title={extended ? `${cond?.label ?? ''} (${modelName(day.model)} extended forecast)`.trim() : cond?.label}
      onClick={(ev) => {
        ev.stopPropagation();
        onOpen(date, (ev.currentTarget as HTMLElement).getBoundingClientRect());
      }}
      className={clsx(
        'flex min-w-0 items-center gap-[5px] overflow-hidden whitespace-nowrap rounded leading-none text-[var(--text-faint)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-muted)]',
        compact ? 'px-[3px] py-[2px] text-[0.68rem]' : 'px-[4px] py-[3px] text-[0.72rem]',
        extended && 'opacity-60'
      )}
    >
      {cond && <WeatherIcon kind={cond.icon} size={compact ? 13 : 15} className="flex-shrink-0" />}
      {items.map((it) => (
        <span key={it.key} className="flex items-center gap-[2px] tabular-nums">
          {it.node}
        </span>
      ))}
    </button>
  );
}

function statItem(
  stat: CellStat,
  d: DayData,
  prefs: WeatherPrefs
): { key: CellStat; node: React.ReactNode } | null {
  const t = prefs.temperature_unit;
  switch (stat) {
    case 'high_low':
      if (d.temperature_2m_max == null) return null;
      return {
        key: stat,
        node: (
          <>
            <span className="text-[var(--text-muted)]">{fmtTemp(d.temperature_2m_max, t)}</span>
            <span>{fmtTemp(d.temperature_2m_min, t)}</span>
          </>
        )
      };
    case 'feels_like':
      if (d.apparent_temperature_max == null) return null;
      return { key: stat, node: <>feels {fmtTemp(d.apparent_temperature_max, t)}</> };
    case 'precip_prob': {
      const p = d.precipitation_probability_max;
      if (p == null || p < 10) return null;
      return {
        key: stat,
        node: (
          <>
            <DropIcon size={9} />
            {Math.round(p)}%
          </>
        )
      };
    }
    case 'precip_sum':
      if (d.precipitation_sum == null || d.precipitation_sum < 0.1) return null;
      return { key: stat, node: fmtPrecip(d.precipitation_sum, prefs.precipitation_unit) };
    case 'snow_sum':
      if (d.snowfall_sum == null || d.snowfall_sum < 0.1) return null;
      return { key: stat, node: <>{fmtSnow(d.snowfall_sum, prefs.precipitation_unit)} snow</> };
    case 'wind_gusts':
      if (d.wind_gusts_10m_max == null) return null;
      return { key: stat, node: <>gusts {fmtWind(d.wind_gusts_10m_max, prefs.wind_speed_unit)}</> };
    case 'uv_max':
      if (d.uv_index_max == null) return null;
      return { key: stat, node: <>UV {Math.round(d.uv_index_max)}</> };
    case 'aqi_max': {
      const v = pickAqi(
        { us_aqi: d.us_aqi_max, european_aqi: d.european_aqi_max },
        prefs.aqi_scale
      );
      if (v == null) return null;
      return { key: stat, node: <>AQI {Math.round(v)}</> };
    }
  }
}
